import React, { useEffect, useCallback, useState } from 'react';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { CancellationService } from 'gw-capability-gateway';
import { DateUtil } from '@xengage/gw-portals-util-js';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import appConfig from 'app-config';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import metadata from './MLCancelPolicyPage.metadata.json5';
import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';

import messages from './MLCancelPolicyPage.messages';
import styles from './MLCancelPolicyPage.module.scss'
import { ScrollToError } from '@jutro/legacy/wizard-next';
import _ from "lodash";
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLPortalSettingsService } from 'gw-capability-quoteandbind';

function MLCancelPolicyPage(props) {
    const {
        updateWizardData, wizardData, goNext
    } = props;
    const { submissionVM } = wizardData;
    const { authHeader, authUserData } = useAuthentication();
    const { MLUserTypes } = appConfig;
    const translator = useTranslator();
    const [showErrors, updateShowErrors] = useState(false);
    const {addErrorAlert, clearErrorAlerts} = MLNotificationBlockUtil()
    const [showCancellationErrors, updateShowCancellationErrors] = useState(false);
    const [showWNSCancellationErrors, updateShowWNSCancellationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const { isComponentValid, onValidate } = useValidation('cancelPolicy');
    const policy = submissionVM.policy.value;
    const {isCancellationRestrictedML = false} = policy
    const POLICY_REWRITTEN_MIDTERM = 'policy_rewritten_midterm_ml';
    const POLICY_REWRITTEN_REPLACED_FLAT = 'policy_rewritten_or_replaced_flat_cancel_ml';
    const POLICY_DECEASED = 'deceased_ext';
    const { refreshData } = useDataRefresh();
    const isGPA = appConfig.portalCode === 'GPA';
    const [enableCancellationRestriction, setEnableCancellationRestriction] = useState(false);

    const ModalService = MLModalService();
    const [cancellationMethodSelected, setCancellationMethodSelected] = useState();

    const addDays = (date, days) => {
        date.setDate(date.getDate() + days);
        return date;
    }

    useEffect(() => {
        if (isGPA) {
            PolChangeTrackEvent('Cancel Step Reached', false, submissionVM, authHeader, { step: 'PolChange Cancel Policy Screen' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

     useEffect(() => {
        MLPortalSettingsService.getSettings(authHeader).then((settings) => {
            if(settings){
                setEnableCancellationRestriction(settings.enableCancellationRestriction_Ext);
            }
        })
    }, [])

    const getDate90DaysBack = () => {
        return addDays(DateUtil.currentDate(), -90);
    };
    const getLocalDate90DaysBack = () => {
        return MLLocalDateUtil.date2LocalDate(getDate90DaysBack());
    };

    const getMinCancellationDate = () => {
        if (submissionVM.cancelReasonCode?.value?.code === POLICY_REWRITTEN_REPLACED_FLAT) {
            return policy.policyTermEffectiveDate;
        }
        if (policy.earliestCancellationDate) {
            const earliestDate = MLLocalDateUtil.localDate2Date(policy.earliestCancellationDate);
            const date90DaysBack = getDate90DaysBack();
            const result = DateUtil.compareIgnoreTime(earliestDate, date90DaysBack);
            return result <= 0 ? getLocalDate90DaysBack() : policy.earliestCancellationDate;
        }
    };

    const showPAAgentCancellationMethodQuestion = () => {
        //state PA and All LOBs
        return submissionVM.baseState.value === 'PA';
    }

    const getMaxCancellationDate = () => {
        if (submissionVM.cancelReasonCode?.value?.code === POLICY_REWRITTEN_REPLACED_FLAT) {
            return policy.policyTermEffectiveDate;
        }
        return policy.policyTermExpirationDate;
    };

    const shouldDisplayReplacementCarrier = useCallback(() => {
        return [POLICY_REWRITTEN_MIDTERM, POLICY_REWRITTEN_REPLACED_FLAT].includes(submissionVM.cancelReasonCode?.value?.code)
    }, [submissionVM.cancelReasonCode?.value]);

    const validateCarrierField = useCallback(() => {
        if(shouldDisplayReplacementCarrier() && _.isEmpty(submissionVM?.carrier_ML?.value)){
            return [messages.missingField]
        }

        return []
    }, [submissionVM.cancelReasonCode?.value, submissionVM.carrier_ML?.value]);

    const onNext = useCallback(() => {
        if (!(submissionVM.aspects.valid && submissionVM.aspects.subtreeValid)) {
            updateShowErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }
        if(isCancellationRestrictedML && ![POLICY_REWRITTEN_MIDTERM, POLICY_REWRITTEN_REPLACED_FLAT,POLICY_DECEASED].includes(submissionVM.cancelReasonCode?.value?.code)){
            window.scrollTo(0,0)
            return;
        }
        const isReasonIsRewrite = shouldDisplayReplacementCarrier()
        if (isReasonIsRewrite) {
            clearErrorAlerts('replacementCarrier')
            clearErrorAlerts('replacementPolicy')
            if(_.isUndefined(submissionVM?.carrier_ML?.value)) {
                updateShowErrors(true);
                addErrorAlert(translator(messages.enterReplacementCarrier), 'replacementCarrier')
                return;
            }else if(validateReplacementPolicyNumber()?.length > 0) {
                updateShowErrors(true);
                addErrorAlert(translator(messages.enterReplacementPolicyNumber), 'replacementPolicy')
                return;
            }
        } else {
            submissionVM.carrier_ML.value = undefined;
        }

        const model = submissionVM.value;
        let promise;
        if (!model.jobNumber) {
            promise = CancellationService.startCancellation(model, authHeader).then((result) => {
                MLTagManagerService.sendFormStepCancellationStep(result);
                // check if scriptparam is enabled
                if(enableCancellationRestriction){
                    // if cancellation can proceed, update the model and go to the next step
                    if(result.canCancellationProceed){
                        submissionVM.value = result;
                        updateWizardData(wizardData);
                        goNext();
                    }
                    // if cancellation cannot proceed, show errors
                    else{
                        updateShowWNSCancellationErrors(true);
                        window.scrollTo(0,0)
                        return;
                    }
                }
                // if scriptparam is not enabled, proceed with cancellation
                else{
                    submissionVM.value = result;
                    updateWizardData(wizardData);
                    goNext();
                }
            });
        }else{
            promise = CancellationService.saveAndQuote(model, authHeader).then((result) => {
                submissionVM.value = result;
                updateWizardData(wizardData);
                goNext();
            });
        }
        promise.catch((error) => {
            error.modalTitle = translator(messages.createErrorTitle);
            error.modalMessage =  translator(messages.createErrorMsg);
            throw error;
        });
        ModalService.showProgressDialog(messages.create, promise);

        if (isGPA) {
            PolChangeTrackEvent('Cancel Step Submitted', false, submissionVM, authHeader, {
                step: 'PolChange Cancel Policy Screen',
                action: 'Cancel Policy Submitted'
            });
        }

    }, [submissionVM, ModalService, isGPA, errorTimestamp, authHeader, updateWizardData, wizardData, goNext, translator]);

    const getNextStepText = () => {
        return (
            <>
                {translator(messages.nextDescription, {
                    strong: (chunks) => <strong>{chunks}</strong>
                })}
            </>
        );
    };

    useEffect(() => {
        Identify('Cancellation Step Reached', {step: 'Effective Date Screen'}, submissionVM, authHeader)        
    }, [])

    const CancelMethodCode = useCallback(() => {
        return [{code: translator(messages.insuredVerballyRequested), name: translator(messages.insuredVerballyRequested)},{code: translator(messages.insuredWrittenRequested), name: translator(messages.insuredWrittenRequested)}]
    }, [messages])
    
    const getReplacementLabel = useCallback(()=>{
        if(["foremost_ext", "bristolwest_ext", "progressive_ml"].includes(submissionVM?.carrier_ML?.value?.code)) {
            return translator(messages.replacementLabelLastFourDigits)
        }

         return translator(messages.replacementLabelFullPolicy)
    }, [submissionVM?.carrier_ML?.value])

    const validateReplacementPolicyNumber = useCallback(()=>{
        const description = submissionVM?.description?.value
        if(!shouldDisplayReplacementCarrier()) {
            return []
        }
        if(_.isEmpty(description) || _.isUndefined(description)) {
            return [translator(messages.missingField)]
        }
        if(description && !/^[a-zA-Z0-9]*$/.test(description)){
            return [translator(messages.onlyAlphaNumbericWithMax, {maxDigit: 20})]
        }
        if(["foremost_ext", "bristolwest_ext", "progressive_ml"].includes(submissionVM?.carrier_ML?.value?.code) && description.length !== 4){
            return [translator(messages.onlyFourDigits)]
        }
        if(!["foremost_ext", "bristolwest_ext", "progressive_ml"].includes(submissionVM?.carrier_ML?.value?.code) && description.length > 20){
            return [translator(messages.max20Digits)]
        }
        return []
    }, [submissionVM])

    const getAvailableCancellationReasons = useCallback(() => {
        const allReasons = _.get(submissionVM, "cancelReasonCode.aspects.availableValues");
        return _.filter(
            allReasons,
            (reason) => {
                if (authUserData.userType === MLUserTypes.PCS) {
                    return true;
                } else {
                    if ([POLICY_DECEASED, POLICY_REWRITTEN_MIDTERM, POLICY_REWRITTEN_REPLACED_FLAT].includes(reason.code)) {
                        return false;
                    } else {
                        return true
                    }
                }
            }
        ).map((filteredReason) => {
            return {
                code: filteredReason.code,
                name: translator({
                    id: filteredReason.name,
                    defaultMessage: filteredReason.name
                })
            };
        });
    }, [authUserData, submissionVM]);

    const overrideProps = {
        effectiveDate: {
            readOnly: submissionVM.jobNumber.value,
            minDate: getMinCancellationDate(),
            maxDate: getMaxCancellationDate()
        },
        initiator: {
            value: translator({id: submissionVM.cancellationSource?.value?.name, defaultMessage: submissionVM.cancellationSource?.value?.name})
        },
        cancelRequestMethod: {
            visible: showPAAgentCancellationMethodQuestion(),
            onValueChange: (value) => {
                const options = CancelMethodCode();
                _.set(submissionVM, 'description', value);
                setCancellationMethodSelected(options.find(opt => opt.code === value))
            },
            availableValues: CancelMethodCode(),
            value: cancellationMethodSelected,
        },
        nextDescription: {
            content: getNextStepText(),
        },
        stickyContinueSection: {
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        },
        carrierRow: {
            visible: shouldDisplayReplacementCarrier(),
        },
        carrier:{
             validationMessages: validateCarrierField(),
        },
        replacementPolicyNumber: {
            label: getReplacementLabel(),
            visible: shouldDisplayReplacementCarrier(),
            validationMessages : validateReplacementPolicyNumber()
        },
        cancelReason: {
            availableValues: getAvailableCancellationReasons(),
            onValueChange: (value) => {
                if(isCancellationRestrictedML && ![POLICY_DECEASED, POLICY_REWRITTEN_MIDTERM, POLICY_REWRITTEN_REPLACED_FLAT].includes(value)) {
                    updateShowCancellationErrors(true)
                    window.scrollTo(0, 0);
                } else {
                    updateShowCancellationErrors(false)
                }
                if (value === POLICY_REWRITTEN_REPLACED_FLAT) {
                    submissionVM.effectiveDate.value = submissionVM.policy.value.policyTermEffectiveDate;
                }
                _.set(submissionVM, 'cancelReasonCode', value);
                refreshData();
            }
        },
        errorBlock: {
            visible: showCancellationErrors,
        },
        errorTitle: {
            content: translator(messages.errorTitle, {
                strong: (chunks) => <strong className='font-weight-bold'>{chunks}</strong>})
        },
        errorMessage1: {
            content: translator(messages.errorMessage1, {
                strong: (chunks) => <strong className='font-weight-bold'>{chunks}</strong>})
        },
        errorMessage2: {
            content: translator(messages.errorMessage2, {
                strong: (chunks) => <strong className='font-weight-bold'>{chunks}</strong>})
        },
        errorMessage3: {
            content: translator(messages.errorMessage3, {
                strong: (chunks) => <strong className='font-weight-bold'>{chunks}</strong>})
        },
        wnsErrorBlock: {
            visible: showWNSCancellationErrors,
        },
        wnsCancellationMsg1: {
            content: translator(messages.wnsCancellationMsg1, {
                strong: (chunks) => <strong className='font-weight-bold'>{chunks}</strong>})
        },
        wnsCancellationMsg2: {
            content: translator(messages.wnsCancellationMsg2)
        },
        wnsCancellationMsg3: {
            content: translator(messages.wnsCancellationMsg3)
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onNext
        }
    }

    return (
        <WizardPage
            showCancel={false}
            showNext={false}
            showPrevious={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
                showErrors={showErrors}
                
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
}

export default MLCancelPolicyPage;